import { useEffect, useState, type ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import type { GlobalSequenceReset } from '../../../../shared/types/appSettings.ts';
import { trpc } from '../../../utils/trpc/trpc.ts';
import { formatTime, parseTime } from './globalSequenceResetTime.ts';


const INITIAL_RESET: GlobalSequenceReset = {
    enabled: false,
    timeMinutes: 0,
};


export function GlobalSequenceReset() {
    const settingQuery = trpc.appSettings.getGlobalSequenceReset.useQuery();
    const setSetting = trpc.appSettings.setGlobalSequenceReset.useMutation();
    const [setting, setSettingLocal] = useState<GlobalSequenceReset | null>(null);
    const [savedSetting, setSavedSetting] = useState<GlobalSequenceReset | null>(null);
    const [timeInput, setTimeInput] = useState('');


    useEffect(() => {
        if (settingQuery.isSuccess) {
            const loadedSetting = settingQuery.data ?? INITIAL_RESET;
            setSettingLocal(loadedSetting);
            setSavedSetting(loadedSetting);
            setTimeInput(formatTime(loadedSetting.timeMinutes));
        }
    }, [settingQuery.data, settingQuery.isSuccess]);

    const saveSetting = async () => {
        const timeMinutes = parseTime(timeInput);
        if (setting == null || timeMinutes == null) return;
        const nextSetting = { ...setting, timeMinutes };
        try {
            await setSetting.mutateAsync(nextSetting);
            setSettingLocal(nextSetting);
            setSavedSetting(nextSetting);
            setTimeInput(formatTime(timeMinutes));
            toast.success('Measurement sequence reset setting saved');
        } catch (error) {
            console.error(error);
            toast.error('Failed to save measurement sequence reset setting');
        }
    };

    const handleEnabledChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (setting == null) return;
        setSettingLocal({ ...setting, enabled: event.target.checked });
    };

    const handleTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setTimeInput(event.target.value);
    };


    if (settingQuery.error) return <div>Failed to load global sequence reset setting</div>;
    if (settingQuery.isLoading || setting == null) return null;

    const parsedTime = parseTime(timeInput);
    const pendingSetting = parsedTime == null ? null : { ...setting, timeMinutes: parsedTime };
    const hasInvalidTime = parsedTime == null;

    return (
        <>
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h2 style={{ margin: 0 }}>Measurement sequence reset</h2>
                </div>
                <p style={{ margin: '8px 0 20px', color: '#555', lineHeight: 1.4 }}>
                    Start the next normal measurement at variant A after the configured time each day.
                </p>

                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
                    <input
                        type='checkbox'
                        checked={setting.enabled}
                        disabled={setSetting.isPending}
                        onChange={handleEnabledChange}
                    />
                    Enabled
                </label>

                <div style={{ marginBottom: '20px' }}>
                    <label htmlFor='global-sequence-reset-time' style={{ display: 'block', width: 'fit-content', marginBottom: '6px', fontWeight: 600 }}>
                        Daily reset time
                    </label>
                    <input
                        id='global-sequence-reset-time'
                        aria-describedby={hasInvalidTime ? 'global-sequence-reset-time-error' : undefined}
                        aria-invalid={hasInvalidTime}
                        style={{ width: '80px' }}
                        type='text'
                        maxLength={5}
                        placeholder='HH:mm'
                        value={timeInput}
                        disabled={setSetting.isPending}
                        onChange={handleTimeChange}
                    />
                    {hasInvalidTime && (
                        <div id='global-sequence-reset-time-error' style={{ marginTop: '6px', color: '#b30000' }}>
                            Enter time as HH:mm (00:00–23:59).
                        </div>
                    )}
                </div>

                <button
                    type='button'
                    disabled={setSetting.isPending || pendingSetting == null || settingsEqual(pendingSetting, savedSetting)}
                    onClick={saveSetting}
                    children={setSetting.isPending ? 'Saving...' : 'Save'}
                />
            </div>
        </>
    );
}

function settingsEqual(a: GlobalSequenceReset | null, b: GlobalSequenceReset | null): boolean {
    return a?.enabled === b?.enabled && a?.timeMinutes === b?.timeMinutes;
}
